/**
 * Шар-ссылка, ведущая на платформу: `<origin платформы>/apps/profitCalculator?share=<id>`.
 *
 * Формат с id в query, а не в пути, — не вкус, а измеренный факт (stage, 2026-09-14):
 * роутер платформы на `/apps/profitCalculator/<что-то>` уходит на `/dashboard`, не
 * монтируя модуль, а query на `/apps/profitCalculator` сохраняет. Подпуть здесь
 * всё же разбирается: как только платформа заведёт wildcard-роут, ссылки вида
 * `/apps/profitCalculator/<id>` заработают без нашего релиза.
 *
 * Модуль видит только `window.location` страницы хоста — `HostContext` подпуть
 * и query не передаёт.
 */

export const PLATFORM_APP_PATH = '/apps/profitCalculator';
export const SHARE_QUERY_PARAM = 'share';

export interface HostLocation {
  origin: string;
  pathname: string;
  search: string;
}

/** Ссылка для получателя. `origin` — страницы хоста: на stage и prod он разный. */
export function buildPlatformShareLink(origin: string, shareId: string): string {
  return `${origin.replace(/\/+$/, '')}${PLATFORM_APP_PATH}?${SHARE_QUERY_PARAM}=${encodeURIComponent(shareId)}`;
}

/**
 * share-id из адреса хоста: сначала `?share=`, затем подпуть после
 * `/apps/profitCalculator/`. Пусто — `null`; пробельный или мусорный id — тоже
 * `null`, чтобы не ходить на бэкенд заведомо ни за чем.
 */
export function readShareIdFromHostLocation(location: Pick<HostLocation, 'pathname' | 'search'>): string | null {
  const fromQuery = new URLSearchParams(location.search).get(SHARE_QUERY_PARAM);
  if (fromQuery !== null) return normalizeShareId(fromQuery);

  const prefix = `${PLATFORM_APP_PATH}/`;
  if (!location.pathname.startsWith(prefix)) return null;
  const rest = location.pathname.slice(prefix.length).split('/')[0];
  return normalizeShareId(decodeURIComponent(rest));
}

/**
 * Адрес хоста без share-id — чтобы «Обновить страницу» не перезатёр правки
 * получателя заново загруженным расчётом. Подпуть тоже срезается до
 * `/apps/profitCalculator`.
 */
export function hostLocationWithoutShare(location: HostLocation): string {
  const params = new URLSearchParams(location.search);
  params.delete(SHARE_QUERY_PARAM);
  const pathname = location.pathname.startsWith(`${PLATFORM_APP_PATH}/`) ? PLATFORM_APP_PATH : location.pathname;
  const search = params.toString();
  return `${location.origin}${pathname}${search ? `?${search}` : ''}`;
}

/**
 * share-id из того, что пользователь вставил в поле: голый id, ссылка платформы
 * (`…/apps/profitCalculator?share=<id>` или `/<id>`) или публичная `…/s/<id>`.
 * Не распознано — `null`.
 */
export function parseShareIdInput(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  let url: URL | null = null;
  try {
    url = new URL(text);
  } catch {
    url = null;
  }
  if (url) {
    const fromPlatform = readShareIdFromHostLocation(url);
    if (fromPlatform) return fromPlatform;
    const publicMatch = url.pathname.match(/\/s\/([^/]+)\/?$/);
    return publicMatch ? normalizeShareId(decodeURIComponent(publicMatch[1])) : null;
  }
  return normalizeShareId(text);
}

function normalizeShareId(raw: string): string | null {
  const id = raw.trim();
  return /^[A-Za-z0-9_-]{1,64}$/.test(id) ? id : null;
}
